import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Center,
  FileButton,
  Flex,
  Group,
  Indicator,
  Menu,
  Modal,
  Paper,
  Portal,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconCamera,
  IconFolder,
  IconPaperclip,
  IconSend,
  IconUpload,
} from "@tabler/icons-react";
import * as df from "date-fns";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "wouter";
import { client } from "@/api-client";
import type { ChatWithOwnerAndTrainerOutput, FileOutput } from "@/api-types";
import { FancyLoader } from "@/components/FancyLoader";
import { useFileUploadModal } from "@/components/FileUploadModal";
import { useFolderNavigator } from "@/components/FolderNavigator";
import { Header } from "@/components/Header";
import { Portrait } from "@/components/Portrait";
import {
  OwnerRoutes,
  TrainerRoutes,
  UniversalRoutes,
  useNavigator,
} from "@/routers/routes";
import { useUser } from "@/UserContext";
import { compareMessagesByDate } from "@/utils/sessions";
import { type ChatMessage, useWebSocket } from "@/websocket/client";

const MAX_MESSAGES = 50;

type Chat = ChatWithOwnerAndTrainerOutput;

const ChatContent = ({
  chat,
  who,
}: {
  chat: Chat;
  who: "trainer" | "owner";
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [endingAt, setEndingAt] = useState<string | undefined>(undefined);
  const [message, setMessage] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const { navigator } = useNavigator();
  const [cameraOpened, { open: openCamera, close: closeCamera }] =
    useDisclosure(false);

  const handleSendFileMessage = async (file: FileOutput) => {
    try {
      const responseMessage = await sendMessage.mutateAsync({
        params: {
          path: {
            engagementId: chat.engagementId,
          },
        },
        body: {
          message: file.name,
          objectId: file.id,
          specialType: "FILE",
        },
      });

      addMessages([responseMessage]);
    } catch (error) {
      console.error("Error sending file message:", error);
    }
  };

  // Use hooks instead of components
  const { open: openFolderNavigator, modal: folderNavigatorModal } =
    useFolderNavigator({
      onFileSelect: handleSendFileMessage,
    });

  const { open: openFileUpload, modal: fileUploadModal } = useFileUploadModal({
    onSuccess: handleSendFileMessage,
  });

  const { data: historicMessages } = client.useQuery(
    "get",
    "/universal/chats/{engagementId}/messages",
    {
      params: {
        path: {
          engagementId: chat.engagementId,
        },
        query: {
          count: MAX_MESSAGES,
          endingAt,
        },
      },
    },
  );

  const { lastMessage } = useWebSocket();

  const interlocutor = who === "trainer" ? chat.owner : chat.trainer;
  const currentUserId = who === "trainer" ? chat.trainerId : chat.ownerId;

  const sendMessage = client.useMutation(
    "post",
    "/universal/chats/{engagementId}/messages",
  );

  const fileUploadMutation = client.useMutation(
    "post",
    "/universal/file-upload",
  );
  const addToLibraryMutation = client.useMutation("post", "/universal/files/");

  const addMessages = useCallback((newMessages: ChatMessage[]) => {
    if (!newMessages.length) {
      return;
    }

    setMessages((prev) => {
      const allMessages = [...newMessages, ...prev];
      allMessages.sort(
        (a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime(),
      );
      const msgs = allMessages.filter(
        (message, index) =>
          allMessages[index - 1]?.id !== message.id &&
          message.id !== "local-new",
      );
      return msgs;
    });
  }, []);

  useEffect(() => {
    if (historicMessages) {
      setHasMore(historicMessages.messages.length >= MAX_MESSAGES);
    }
    const newMessages = [...(historicMessages?.messages ?? [])];

    if (lastMessage && lastMessage.type === "chat_message") {
      newMessages.push(lastMessage.content as ChatMessage);
    }

    addMessages(newMessages);

    if (isFirstLoad) {
      setIsFirstLoad(false);
      setShouldScrollToBottom(true);
    } else {
      const element = scrollAreaRef.current?.querySelector(
        ".mantine-ScrollArea-viewport",
      );
      if (!element) {
        return;
      }
      if (
        element.scrollTop + element.clientHeight >=
        element.scrollHeight - 20
      ) {
        setShouldScrollToBottom(true);
      }
    }
  }, [historicMessages, lastMessage, addMessages, isFirstLoad]);

  useLayoutEffect(() => {
    if (!scrollAreaRef.current || !shouldScrollToBottom) {
      return;
    }

    const viewport = scrollAreaRef.current.querySelector(
      ".mantine-ScrollArea-viewport",
    );
    if (viewport) {
      viewport.scrollTop = viewport.scrollHeight;
      setShouldScrollToBottom(false);
    }
  }, [shouldScrollToBottom]);

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    setMessage("");
    setMessages((prev) => [
      ...prev,
      {
        id: "local-new",
        engagementId: chat.engagementId,
        sentAt: new Date().toUTCString(),
        content: message,
        senderId: currentUserId,
        type: "USER_MESSAGE",
      },
    ]);
    setShouldScrollToBottom(true);

    const responseMessage = await sendMessage.mutateAsync({
      params: {
        path: {
          engagementId: chat.engagementId,
        },
      },
      body: {
        message: message,
        specialType: null,
      },
    });

    addMessages([responseMessage]);
  };

  const handleTakePicture = async (file: File | null) => {
    if (!file) return;

    try {
      closeCamera();

      const fileUpload = await fileUploadMutation.mutateAsync({});
      const formData = new FormData();

      Object.entries(fileUpload.params.fields).forEach(([k, v]) =>
        formData.append(k, v),
      );
      formData.append("file", file);

      // Upload file
      await fetch(fileUpload.params.url, {
        method: "POST",
        body: formData,
      });

      // Add to library
      const result = await addToLibraryMutation.mutateAsync({
        body: {
          uploadId: fileUpload.id,
          originalName: file.name,
          name: `Photo_${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}`,
          description: "Photo taken from chat",
          type: "IMAGE",
          parentFolderId: null,
          isPublic: false,
        },
      });

      // Send the file message
      if (result) {
        await handleSendFileMessage(result);
      }
    } catch (error) {
      console.error("Error uploading picture:", error);
    }
  };

  const loadMore = () => {
    if (messages.length > 0) {
      setEndingAt(messages[0]?.sentAt);
    }
  };

  const formatDate = (date: Date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    }
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const renderMessageBody = (message: ChatMessage, isCurrentUser: boolean) => {
    const who = isCurrentUser ? "You" : interlocutor.firstName;
    if (message.specialType === "FILE") {
      return (
        <Text fs="italic">
          {`${who} sent a file: `}
          <Anchor
            td="underline"
            c={isCurrentUser ? "white" : "black"}
            onClick={navigator({
              path: UniversalRoutes.FILE_PREVIEW,
              // biome-ignore lint/style/noNonNullAssertion: objectId is mandatory for files
              params: { fileId: message.objectId! },
            })}
          >
            {message.content}
          </Anchor>
        </Text>
      );
    }
    if (message.specialType === "CANCEL_SESSION") {
      return (
        <>
          <Text fs="italic">
            {`${who} asked to cancel `}
            <Anchor
              td="underline"
              c={isCurrentUser ? "white" : "black"}
              onClick={navigator({
                path: UniversalRoutes.SESSIONS.DETAIL,
                // biome-ignore lint/style/noNonNullAssertion: objectId is mandatory for files
                params: { sessionId: message.objectId! },
              })}
            >
              the session
            </Anchor>
            {":"}
          </Text>
          <Text>{message.content}</Text>
        </>
      );
    }
    return <Text>{message.content}</Text>;
  };

  return (
    <Stack>
      <Header
        title={`Chat with ${interlocutor.firstName}`}
        actionControl={
          <Portrait
            profile={interlocutor}
            size="md"
            onClick={navigator({
              path:
                who === "trainer"
                  ? TrainerRoutes.CLIENTS.DETAIL
                  : OwnerRoutes.TRAINER_ENGAGEMENT,
              params: { engagementId: chat.engagementId },
            })}
          />
        }
      />
      <ScrollArea h={"70vh"} ref={scrollAreaRef}>
        <Stack ref={chatRef}>
          {hasMore && (
            <Center>
              <Anchor c="blue.8" onClick={loadMore}>
                Load older
              </Anchor>
            </Center>
          )}
          {messages.flatMap((message, index) => {
            const isCurrentUser = message.senderId === currentUserId;
            const currentDate = new Date(message.sentAt);
            const previousMessage = messages[index - 1];
            const previousDate = previousMessage
              ? new Date(previousMessage.sentAt)
              : null;
            const showDateDivider =
              !previousDate || !df.isSameDay(currentDate, previousDate);

            return [
              showDateDivider && (
                <Center key={`divider-${message.id}`} mb="xs">
                  <Text size="sm" c="gray.6">
                    {formatDate(currentDate)}
                  </Text>
                </Center>
              ),
              <Box
                key={message.id}
                style={{
                  alignSelf: isCurrentUser ? "flex-end" : "flex-start",
                }}
              >
                <Paper
                  p="sm"
                  radius="lg"
                  bg={isCurrentUser ? "blue" : "gray.1"}
                  c={isCurrentUser ? "white" : "black"}
                  style={{
                    wordBreak: "break-word",
                  }}
                >
                  {renderMessageBody(message, isCurrentUser)}
                  <Text size="xs" ta="right">
                    {new Date(message.sentAt).toLocaleTimeString(undefined, {
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </Text>
                </Paper>
              </Box>,
            ];
          })}
        </Stack>
      </ScrollArea>
      <Group>
        <TextInput
          placeholder="Type a message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          style={{ flex: 1 }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSendMessage();
            }
          }}
        />
        <ActionIcon
          color="blue"
          variant="filled"
          size="lg"
          radius="xl"
          onClick={handleSendMessage}
          disabled={!message.trim()}
        >
          <IconSend size="1.2em" />
        </ActionIcon>
        {who === "trainer" && (
          <Menu shadow="md" width={200} position="top">
            <Menu.Target>
              <ActionIcon color="blue" variant="filled" size="lg" radius="xl">
                <IconPaperclip size="1.2em" />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconFolder size={14} />}
                onClick={openFolderNavigator}
              >
                Pick from Library
              </Menu.Item>
              <Menu.Item
                leftSection={<IconUpload size={14} />}
                onClick={openFileUpload}
              >
                Upload File
              </Menu.Item>
              <Menu.Item
                leftSection={<IconCamera size={14} />}
                onClick={openCamera}
              >
                Take Picture
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>

      <Portal>
        <Modal
          opened={cameraOpened}
          onClose={closeCamera}
          title="Take a Picture"
          centered
          size="sm"
        >
          <Stack align="center" gap="md">
            <Text size="sm" c="dimmed" ta="center">
              Take a picture to send in the chat
            </Text>
            <FileButton onChange={handleTakePicture} accept="image/*" capture>
              {(props) => (
                <Button {...props} variant="filled" size="lg">
                  Take Photo
                </Button>
              )}
            </FileButton>
            <FileButton onChange={handleTakePicture} accept="image/*">
              {(props) => (
                <Button {...props} variant="outline" size="lg">
                  Choose from Gallery
                </Button>
              )}
            </FileButton>
          </Stack>
        </Modal>

        {/* Render modals from hooks */}
        {folderNavigatorModal}
        {fileUploadModal}
      </Portal>
    </Stack>
  );
};

export const Chat = ({ who }: { who: "trainer" | "owner" }) => {
  const { engagementId } = useParams<{ engagementId: string }>();
  const { data: chat, isLoading } = client.useQuery(
    "get",
    "/universal/chats/{engagementId}",
    {
      params: { path: { engagementId } },
    },
  );

  const lastSeenMutation = client.useMutation(
    "post",
    "/universal/chats/{engagementId}/last-seen",
  );

  useEffect(() => {
    if (!chat) {
      return;
    }

    lastSeenMutation.mutate({
      params: { path: { engagementId } },
    });
  }, [chat, engagementId, lastSeenMutation.mutate]);

  if (isLoading || !chat) {
    return <FancyLoader />;
  }

  return <ChatContent chat={chat} who={who} />;
};

export const ChatList = ({ who }: { who: "trainer" | "owner" }) => {
  const user = useUser();
  const { lastMessage: lastMessageRaw } = useWebSocket();
  const { navigator } = useNavigator();
  const { data: chats, isLoading } = client.useQuery(
    "get",
    "/universal/chats/",
  );

  const [chatsWithLastMessage, setChatsWithLastMessage] = useState<
    Record<string, ChatMessage>
  >({});

  const lastMessage =
    lastMessageRaw &&
    lastMessageRaw.type === "chat_message" &&
    lastMessageRaw.content;

  useEffect(() => {
    if (!lastMessage) {
      return;
    }

    setChatsWithLastMessage((prev) => ({
      ...prev,
      [lastMessage.engagementId]: lastMessage,
    }));
  }, [lastMessage]);

  // Update chats with lastMessage from websocket if available
  const newChats = useMemo(() => {
    if (!chats) {
      return [];
    }

    const updatedChats = chats.chats.map((chat) => {
      const potentialLastMessage = chatsWithLastMessage[chat.engagementId];
      if (potentialLastMessage) {
        return {
          ...chat,
          lastMessage: potentialLastMessage,
          lastMessageAt: potentialLastMessage.sentAt,
        };
      }
      return chat;
    });

    return updatedChats.sort(compareMessagesByDate);
  }, [chats, chatsWithLastMessage]);

  if (isLoading || !chats) {
    return <FancyLoader />;
  }

  const formatDate = (dateStr: string) => {
    const date = df.parseISO(dateStr);

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (df.isSameDay(date, today)) {
      return date.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      });
    }

    if (df.isSameDay(date, yesterday)) {
      return "Yesterday";
    }
    return date.toLocaleDateString();
  };

  const showIndicator = (chat: (typeof newChats)[number]) =>
    chat.lastMessage?.senderId !== user.accountId &&
    chat.lastMessage &&
    ((who === "owner" && chat.lastMessage.sentAt > chat.lastSeenAtOwner) ||
      (who === "trainer" && chat.lastMessage.sentAt > chat.lastSeenAtTrainer));

  const chatListContent =
    newChats.length > 0 ? (
      newChats.map((chat) => (
        <Paper
          key={chat.engagementId}
          onClick={navigator({
            path: UniversalRoutes.CHAT.DETAIL,
            params: { engagementId: chat.engagementId },
          })}
        >
          <Flex gap="sm" align="center">
            <Indicator
              color="accent.3"
              position="top-start"
              disabled={!showIndicator(chat)}
            >
              <Portrait
                profile={who === "trainer" ? chat.owner : chat.trainer}
                size="lg"
              />
            </Indicator>
            <Box
              style={{
                flex: "1 1 auto",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              <Group justify="space-between">
                <Text fw="bold">
                  {who === "trainer"
                    ? chat.owner.firstName
                    : chat.trainer.firstName}
                </Text>
                {chat.lastMessage && (
                  <Text size="sm" c="gray.8">
                    {formatDate(chat.lastMessage.sentAt)}
                  </Text>
                )}
              </Group>
              {chat.lastMessage && (
                <Text
                  c="gray.8"
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {chat.lastMessage.senderId === user.accountId && "You: "}
                  {chat.lastMessage.content}
                </Text>
              )}
            </Box>
          </Flex>
        </Paper>
      ))
    ) : (
      <Center>
        <Paper p="md" radius="lg" withBorder>
          <Text ta="center">
            You will be able to communicate with clients once they sign up and
            connect with you.
          </Text>
        </Paper>
      </Center>
    );

  return (
    <Stack>
      <Title order={1}>Messages</Title>
      {chatListContent}
    </Stack>
  );
};
